/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import HFIcon from '../icons';
import { skipReasonLabel } from '../derivations';
import clearTextSelection from '../domSelection';
import InputClearButton from '../../../../components/MinorUtilities/InputClearButton/InputClearButton';

export default function HFUnroutedDock({
   items,
   draggingId = null,
   selectedItemId = null,
   canDrag = true,
   isDropTarget = false,
   onDragStart,
   onDragEnd,
   onDockDragOver = null,
   onDockDrop = null,
   onItemOpen = null,
   pointsHidden = false,
   onTogglePointsVisibility = null,
}) {
   // Itens pendentes sem classe de carga definida (cargo_class) — feedback pro
   // operador classificar antes de roteirizar/otimizar (handoff 0032).
   const naCount = items.filter((u) => !u.cargoClass).length;
   // Itens sem serviço atribuído (serviceTotal === 0) — alerta bloqueante (barra
   // a otimização), mesma semântica da tag "Sem serviço(s)" do card.
   const noServiceCount = items.filter((u) => u.serviceTotal === 0).length;
   // Itens sem coordenada (needs_geocoding, handoff 0043) — barram a otimização
   // até o operador fixar o ponto no mapa (ficha → Definir localização).
   const geoCount = items.filter((u) => u.needsGeocoding).length;

   // Colapsado, o dock vira só o cabeçalho — libera a leitura do mapa.
   // Continua aceitando drop (expande visualmente via outline do alvo).
   const [isCollapsed, setIsCollapsed] = useState(false);

   // ─── Filtro de subconjunto + busca (local, sobre os itens já carregados) ──
   // O dock filtra CLIENT-SIDE o que o snapshot já trouxe (não chama backend).
   //   filter: 'all' | 'noclass' | 'noservice' | 'noloc'  → subconjunto exibido.
   //   searchApplied: termo confirmado (pedido OU cliente). Busca EXPLÍCITA —
   //     só aplica no clique da lupa / Enter (sem debounce, como em /routing/items).
   const [filter, setFilter] = useState('all');
   const [searchDraft, setSearchDraft] = useState('');
   const [searchApplied, setSearchApplied] = useState('');

   const filteredItems = useMemo(() => {
      let list = items;
      if (filter === 'noclass') list = list.filter((u) => !u.cargoClass);
      else if (filter === 'noservice')
         list = list.filter((u) => u.serviceTotal === 0);
      else if (filter === 'noloc') list = list.filter((u) => u.needsGeocoding);
      const q = searchApplied.trim().toLowerCase();
      if (q) {
         list = list.filter(
            (u) =>
               String(u.id ?? '').toLowerCase().includes(q) ||
               String(u.nm ?? '').toLowerCase().includes(q)
         );
      }
      return list;
   }, [items, filter, searchApplied]);

   const handleSearchSubmit = (e) => {
      e.preventDefault();
      setSearchApplied(searchDraft);
   };

   // Limpar a busca numa ação única: zera rascunho E aplicado, então a lista
   // volta a exibir tudo na hora (filteredItems reage a searchApplied) sem o
   // usuário precisar apagar o texto e clicar na lupa de novo.
   const handleClearSearch = () => {
      setSearchDraft('');
      setSearchApplied('');
   };

   // O dock aceita drop de itens vindos de rota (technician_id=null no
   // reorder). onDockDragOver é null quando não há drag de rota em curso.
   const handleDragOver = onDockDragOver
      ? (e) => {
           e.preventDefault();
           e.dataTransfer.dropEffect = 'move';
           onDockDragOver();
        }
      : undefined;

   const handleDrop = onDockDrop
      ? (e) => {
           e.preventDefault();
           onDockDrop();
        }
      : undefined;

   return (
      <div
         className={`hf-dock ${isDropTarget ? 'drop-target' : ''} ${
            isCollapsed ? 'collapsed' : ''
         }`}
         onDragOver={handleDragOver}
         onDrop={handleDrop}
      >
         <div className="hf-dock-head">
            <div className="ttl-row">
               <div className="ttl">
                  Não roteirizados <span className="badge">{items.length}</span>
               </div>
               <div className="head-actions">
                  {/* Olho — mostra/oculta os PONTOS não-roteirizados no mapa
                     (espelha o toggle do painel de rotas). */}
                  {onTogglePointsVisibility && items.length > 0 && (
                     <button
                        type="button"
                        className={`vis-btn ${pointsHidden ? 'off' : ''}`}
                        onClick={onTogglePointsVisibility}
                        title={
                           pointsHidden
                              ? 'Mostrar pontos não roteirizados no mapa'
                              : 'Ocultar pontos não roteirizados no mapa'
                        }
                        aria-label={
                           pointsHidden
                              ? 'Mostrar pontos não roteirizados no mapa'
                              : 'Ocultar pontos não roteirizados no mapa'
                        }
                     >
                        {pointsHidden ? HFIcon.eyeOff : HFIcon.eye}
                     </button>
                  )}
                  <button
                     type="button"
                     className="collapse-btn"
                     onClick={() => setIsCollapsed((prev) => !prev)}
                     title={isCollapsed ? 'Expandir' : 'Recolher'}
                     aria-label={
                        isCollapsed
                           ? 'Expandir não roteirizados'
                           : 'Recolher não roteirizados'
                     }
                  >
                     {isCollapsed ? HFIcon.chevU : HFIcon.chevD}
                  </button>
               </div>
            </div>
            <div className="filters">
               <button
                  type="button"
                  className={`f ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
               >
                  Todos
               </button>
               {noServiceCount > 0 && (
                  <button
                     type="button"
                     className={`f restr ${
                        filter === 'noservice' ? 'active' : ''
                     }`}
                     title="Itens sem serviço atribuído — barram a otimização"
                     onClick={() =>
                        setFilter((f) =>
                           f === 'noservice' ? 'all' : 'noservice'
                        )
                     }
                  >
                     Sem serviço(s) · {noServiceCount}
                  </button>
               )}
               {geoCount > 0 && (
                  <button
                     type="button"
                     className={`f restr ${filter === 'noloc' ? 'active' : ''}`}
                     title="Itens sem coordenada — defina a localização antes de otimizar"
                     onClick={() =>
                        setFilter((f) => (f === 'noloc' ? 'all' : 'noloc'))
                     }
                  >
                     Sem localização · {geoCount}
                  </button>
               )}
               {/* "Sem classe" é ATENÇÃO (amarelo), não bloqueio: classe de carga
                  não é payload obrigatório pra otimizar. Fica por último. */}
               {naCount > 0 && (
                  <button
                     type="button"
                     className={`f attn ${filter === 'noclass' ? 'active' : ''}`}
                     title="Itens sem classe de carga definida (não bloqueia a otimização)"
                     onClick={() =>
                        setFilter((f) => (f === 'noclass' ? 'all' : 'noclass'))
                     }
                  >
                     Sem classe · {naCount}
                  </button>
               )}
            </div>
            <form className="hf-dock-search" onSubmit={handleSearchSubmit}>
               <div className="search-field">
                  <input
                     type="text"
                     value={searchDraft}
                     onChange={(e) => setSearchDraft(e.target.value)}
                     placeholder="Buscar por nº pedido ou nome do cliente..."
                     aria-label="Buscar por nº pedido ou nome do cliente"
                  />
                  <InputClearButton
                     visible={searchDraft !== '' || searchApplied !== ''}
                     side="right"
                     hoverText="Limpar"
                     onClear={handleClearSearch}
                  />
               </div>
               <button
                  type="submit"
                  title="Pesquisar"
                  aria-label="Pesquisar"
                  disabled={items.length === 0}
               >
                  {HFIcon.search}
               </button>
            </form>
         </div>
         <div className="hf-dock-list scroll">
            {filteredItems.map((u) => {
               // Item travado (M2): tem evento ativo → é âncora. Não arrasta
               // (o backend recusaria) — o duplo-clique abre a ficha, que é a
               // via de destravamento (remover evento LIFO).
               const isLocked = Boolean(u.locked);
               const canDragCard = canDrag && !isLocked;
               const noService = u.serviceTotal === 0;
               // Motivo de não-roteirização do otimizador (handoff 0049). AVISO,
               // não trava — o card segue arrastável; a tag só informa o porquê.
               const skipReason = skipReasonLabel(u.optimizationSkipReason);
               const hasFoot =
                  u.carOnly ||
                  isLocked ||
                  u.win ||
                  u.needsGeocoding ||
                  noService ||
                  skipReason;
               const isSelected =
                  selectedItemId != null && u.itemId === selectedItemId;
               return (
                  <div
                     key={u.id}
                     className={`hf-dock-card ${
                        draggingId === u.id ? 'dragging' : ''
                     } ${isLocked ? 'locked' : ''} ${
                        isSelected ? 'selected' : ''
                     }`}
                     onDoubleClick={onItemOpen ? () => onItemOpen(u) : undefined}
                     onMouseUp={clearTextSelection}
                     role={onItemOpen ? 'button' : undefined}
                     tabIndex={onItemOpen ? 0 : undefined}
                     onKeyDown={
                        onItemOpen
                           ? (e) => {
                                if (e.key === 'Enter') onItemOpen(u);
                             }
                           : undefined
                     }
                     title={onItemOpen ? 'Duplo-clique para abrir a ficha' : undefined}
                  >
                     {/* Alça de arraste — única área que inicia o drag-n-drop
                        (evita drag acidental ao clicar no card). Azul no hover
                        quando o item pode ser arrastado. */}
                     <button
                        type="button"
                        className="hf-drag-handle"
                        aria-label="Arrastar para roteirizar"
                        title={
                           canDragCard
                              ? 'Arraste para roteirizar'
                              : 'Item travado — não pode ser arrastado'
                        }
                        draggable={canDragCard}
                        disabled={!canDragCard}
                        onClick={(e) => e.stopPropagation()}
                        onDragStart={(e) => {
                           if (!canDragCard) return;
                           // Necessário no Firefox para permitir o drag.
                           e.dataTransfer.setData('text/plain', u.id);
                           e.dataTransfer.effectAllowed = 'move';
                           // Preview = card inteiro (não só a alça).
                           const card = e.currentTarget.closest('.hf-dock-card');
                           if (card) e.dataTransfer.setDragImage(card, 24, 20);
                           onDragStart({ source: 'dock', id: u.id, item: u });
                        }}
                        onDragEnd={onDragEnd}
                     >
                        {HFIcon.grip}
                     </button>
                     {/* Cabeçalho: nº do pedido em destaque (esquerda) +
                        bloco de carga rotulado (canto superior direito). */}
                     <div className="head">
                        <span className="order">PED-{u.id}</span>
                        <div className="cargo-block">
                           <span className="cargo-label">Carga</span>
                           <span
                              className={`cargo-badge ${
                                 u.cargoClass ? `c-${u.cargoClass}` : 'na'
                              }`}
                              title={
                                 u.cargoClass
                                    ? `Classe de carga: ${u.cargoClass}`
                                    : 'Classe de carga não definida — abra a ficha (duplo-clique) para classificar.'
                              }
                           >
                              {u.cargoClass || 'N/A'}
                           </span>
                        </div>
                     </div>
                     {/* Nome do cliente ocupa a antiga linha de endereço; o
                        endereço (campo longo) fica só na ficha detalhada. */}
                     <div className="cust">{u.nm}</div>
                     {hasFoot && (
                        <div className="foot">
                           <div className="foot-tags">
                              {u.carOnly && (
                                 <span
                                    className="caronly-tag"
                                    title="Restrição: este item só pode ir em carro."
                                 >
                                    SÓ CARRO
                                 </span>
                              )}
                              {isLocked && (
                                 <span
                                    className="lock-tag"
                                    title="Item travado — tem evento ativo. Abra a ficha (duplo-clique) para remover o evento e destravar."
                                 >
                                    {HFIcon.lock} TRAVADO
                                 </span>
                              )}
                              {u.win && <span className="win">{u.win}</span>}
                           </div>
                           <div className="foot-alerts">
                              {noService && (
                                 <span
                                    className="noservice-tag"
                                    title="Item sem serviço atribuído — barra a otimização. Abra a ficha (duplo-clique) para atribuir."
                                 >
                                    Sem serviço(s)
                                 </span>
                              )}
                              {u.needsGeocoding && (
                                 <span
                                    className="geo-tag"
                                    title="Item sem geolocalização — barra a otimização. Abra a ficha (duplo-clique) para definir o ponto no mapa."
                                 >
                                    Sem geolocalização
                                 </span>
                              )}
                              {skipReason && (
                                 <span
                                    className="skip-tag"
                                    title={`${skipReason}. Aviso, não bloqueio — arraste para uma rota mesmo assim, se quiser.`}
                                 >
                                    {HFIcon.clock} {skipReason}
                                 </span>
                              )}
                           </div>
                        </div>
                     )}
                  </div>
               );
            })}
            {filteredItems.length === 0 && (
               <div className="hf-dock-card" style={{ cursor: 'default' }}>
                  <div className="ad">
                     {items.length === 0
                        ? 'Nenhum item aguardando roteirização.'
                        : 'Nenhum item para este filtro/busca.'}
                  </div>
               </div>
            )}
         </div>
      </div>
   );
}

HFUnroutedDock.propTypes = {
   items: PropTypes.array.isRequired,
   draggingId: PropTypes.string,
   selectedItemId: PropTypes.number,
   canDrag: PropTypes.bool,
   isDropTarget: PropTypes.bool,
   onDragStart: PropTypes.func.isRequired,
   onDragEnd: PropTypes.func.isRequired,
   onDockDragOver: PropTypes.func,
   onDockDrop: PropTypes.func,
   onItemOpen: PropTypes.func,
   pointsHidden: PropTypes.bool,
   onTogglePointsVisibility: PropTypes.func,
};
